import { leaguePath, teamPath } from '../utils/siteUrls';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SportsSoccerRoundedIcon from '@mui/icons-material/SportsSoccerRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import { leagueImages, LEAGUE_IMAGE_SIZES, HOME_HERO_LARGE, HOME_HERO_SRC_SET, HOME_HERO_SIZES } from '../constants/homeImages';
import { useCompetitions } from './CompetitionContext';
import { fetchArticles, formatArticleDate } from '../services/strapiBlog';
import { fetchTeams } from '../services/api';

const defaultArticleImage = '/images/symuliga.webp';

const actionTiles = [
  {
    title: 'Rozgrywki',
    description: 'Tabele, terminarze, wyniki i symulacje lig.',
    path: '#rozgrywki',
    icon: SportsSoccerRoundedIcon,
    color: '#35f0aa',
  },
  {
    title: 'Ranking Elo',
    description: 'Siła drużyn, regiony i poziomy rozgrywek.',
    path: '/elo-ranking',
    icon: EmojiEventsRoundedIcon,
    color: '#ffd54f',
  },
  {
    title: 'Wykresy',
    description: 'Trendy formy, Elo i porównania zespołów.',
    path: '/team/1/analytics?empty=1',
    icon: BarChartRoundedIcon,
    color: '#4fc3f7',
  },
  {
    title: 'Analiza kursów',
    description: 'Szanse, kursy i miejsca, gdzie model widzi value.',
    path: '/odds',
    icon: PaidRoundedIcon,
    color: '#ffca28',
  },
];

const normalizeSearchText = (value = '') =>
  value
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .replace(/\s+/g, ' ');

const hasCompetitionGroup = (group) => {
  const value = (group || '').toString().trim();
  return value && value.toLowerCase() !== 'null' && value !== 'Brak';
};

const competitionLabel = (competition) =>
  hasCompetitionGroup(competition.group)
    ? `${competition.name} (${competition.group.trim()})`
    : competition.name;

const competitionUrl = (competition) =>
  leaguePath(competition.competition_id, competition);

const findCompetition = (competitions, matcher) =>
  Object.values(competitions || {}).flat().find(matcher);

const SectionHeader = ({ eyebrow, title, action, actionTo }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
    justifyContent="space-between"
    spacing={1.5}
    sx={{ mb: 2 }}
  >
    <Box>
      {eyebrow && (
        <Typography sx={{ color: 'accent.green', fontSize: '0.78rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0 }}>
          {eyebrow}
        </Typography>
      )}
      <Typography variant="h5" component="h2" sx={{ fontWeight: 900 }}>
        {title}
      </Typography>
    </Box>
    {actionTo && (
      <Button
        component={Link}
        to={actionTo}
        endIcon={<ArrowForwardRoundedIcon />}
        sx={{ color: 'accent.green', textTransform: 'none', alignSelf: { xs: 'stretch', sm: 'auto' } }}
      >
        {action}
      </Button>
    )}
  </Stack>
);

const isClassLeagueQuery = (query) => {
  if (query === 'a klasa' || query === 'klasa a') return 'a';
  if (query === 'b klasa' || query === 'klasa b') return 'b';
  return null;
};

const matchesCompetitionQuery = (competition, query, queryParts) => {
  const label = normalizeSearchText(competitionLabel(competition));
  const classQuery = isClassLeagueQuery(query);

  if (classQuery) {
    return label.includes(`${classQuery} klasa`) || label.includes(`klasa ${classQuery}`);
  }

  return queryParts.every((part) => {
    if (part.length === 1) return label.split(' ').includes(part);
    return label.includes(part);
  });
};

const GlobalSearch = ({ teams, teamsLoading, teamsError, competitions, activeSeason }) => {
  const navigate = useNavigate();
  const [value, setValue] = useState('');

  const options = useMemo(() => {
    const query = normalizeSearchText(value);
    if (query.length < 2) return [];
    const queryParts = query.split(' ').filter(Boolean);

    const teamOptions = teams
      .filter((team) => queryParts.every((part) => team.normalizedName.includes(part)))
      .sort((a, b) => {
        const aStarts = a.normalizedName.startsWith(query) ? 0 : 1;
        const bStarts = b.normalizedName.startsWith(query) ? 0 : 1;
        if (aStarts !== bStarts) return aStarts - bStarts;
        return (b.eloRanking || 0) - (a.eloRanking || 0);
      })
      .slice(0, 8)
      .map((team) => ({
        id: `team-${team.teamId}`,
        type: 'Klub',
        label: team.teamName,
        helper: team.competitionName || 'Profil drużyny',
        path: teamPath(team.teamId, team.teamName),
      }));

    const leagueOptions = Object.values(competitions || {})
      .flat()
      .filter((competition) =>
        competition.season === activeSeason &&
        competition.type === 'league' &&
        matchesCompetitionQuery(competition, query, queryParts)
      )
      .slice(0, 8)
      .map((competition) => ({
        id: `competition-${competition.competition_id}`,
        type: 'Liga',
        label: competitionLabel(competition),
        helper: competition.country === 'Poland' ? 'Rozgrywki polskie' : competition.country,
        path: competitionUrl(competition),
      }));

    return [...teamOptions, ...leagueOptions];
  }, [value, teams, competitions, activeSeason]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: '1px solid rgba(53, 240, 170, 0.22)',
        bgcolor: 'rgba(9, 18, 19, 0.78)',
        boxShadow: '0 22px 60px rgba(0,0,0,0.28)',
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontWeight: 900, fontSize: { xs: '1rem', sm: '1.12rem' } }}>
            Znajdź klub lub ligę
          </Typography>
        </Stack>
        <Autocomplete
          freeSolo
          autoHighlight
          loading={teamsLoading}
          options={options}
          inputValue={value}
          onInputChange={(event, nextValue) => setValue(nextValue)}
          onChange={(event, selected) => {
            if (selected && typeof selected !== 'string') navigate(selected.path);
          }}
          filterOptions={(items) => items}
          groupBy={(option) => option.type}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
          noOptionsText={value.trim().length < 2 ? 'Wpisz co najmniej 2 znaki' : 'Brak wyników'}
          slotProps={{
            popper: {
              sx: { zIndex: 3000 },
              modifiers: [{ name: 'flip', enabled: true }],
            },
            paper: {
              sx: {
                '& .MuiAutocomplete-listbox': {
                  maxHeight: 360,
                },
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="np. Hutnik, Polonia, Ekstraklasa, III liga..."
              error={Boolean(teamsError)}
              helperText={teamsError || ' '}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>{option.label}</Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>{option.helper}</Typography>
              </Box>
            </Box>
          )}
        />
      </Stack>
    </Paper>
  );
};

const LeagueCard = ({ title, image, competition, fallbackPath }) => {
  const basePath = competition ? competitionUrl(competition) : fallbackPath;
  const actionBasePath = competition
    ? `/liga/${competition.competition_id}`
    : fallbackPath.startsWith('/liga/')
      ? fallbackPath.match(/^\/liga\/\d+/)?.[0] || fallbackPath
      : fallbackPath;

  return (
    <Card
      sx={{
        height: '100%',
        bgcolor: 'background.surface',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <CardActionArea component={Link} to={basePath} sx={{ display: 'block' }}>
        <CardMedia component="img" height="128" loading="lazy" decoding="async" image={image.src} srcSet={image.srcSet} sizes={LEAGUE_IMAGE_SIZES} alt={title} sx={{ objectFit: 'cover' }} />
      </CardActionArea>
      <CardContent sx={{ p: 1.75 }}>
        <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
          {title}
        </Typography>
        {actionBasePath.startsWith('/liga/') ? (
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
            <Button component={Link} to={`${actionBasePath}/leagueTables`} size="small" startIcon={<TableChartRoundedIcon />} variant="outlined">
              Tabela
            </Button>
            <Button component={Link} to={`${actionBasePath}/fixtures`} size="small" startIcon={<CalendarMonthRoundedIcon />} variant="outlined">
              Terminarz
            </Button>
            <Button component={Link} to={`${actionBasePath}/summary`} size="small" startIcon={<QueryStatsRoundedIcon />} variant="outlined">
              Predykcja
            </Button>
          </Stack>
        ) : (
          <Button component={Link} to={basePath} size="small" endIcon={<ArrowForwardRoundedIcon />} sx={{ mt: 1.5, color: 'accent.green' }}>
            Przejdź
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

const QuickAction = ({ tile }) => {
  const Icon = tile.icon;
  const isAnchor = tile.path.startsWith('#');

  return (
    <Paper
      component={isAnchor ? 'a' : Link}
      href={isAnchor ? tile.path : undefined}
      to={isAnchor ? undefined : tile.path}
      elevation={0}
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        minHeight: 104,
        color: 'text.primary',
        textDecoration: 'none',
        borderRadius: 2,
        border: '1px solid rgba(255,255,255,0.08)',
        bgcolor: 'background.surface',
        transition: 'transform 160ms ease, border-color 160ms ease, background-color 160ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: 'rgba(53, 240, 170, 0.35)',
          bgcolor: 'background.surfaceElevated',
        },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          display: 'grid',
          placeItems: 'center',
          borderRadius: 2,
          bgcolor: `${tile.color}1f`,
          color: tile.color,
          flexShrink: 0,
        }}
      >
        <Icon />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 900 }}>{tile.title}</Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.84rem', lineHeight: 1.35 }}>
          {tile.description}
        </Typography>
      </Box>
    </Paper>
  );
};

const HomePage = () => {
  const { competitions, activeSeason } = useCompetitions();
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const [articlesError, setArticlesError] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [teamsError, setTeamsError] = useState(null);

  const featuredCompetitions = useMemo(() => {
    const ekstraklasa = findCompetition(
      competitions,
      (competition) => competition.season === activeSeason && Number(competition.competition_id) === 432
    ) || findCompetition(competitions, (competition) => competition.season === activeSeason && competition.name === 'Ekstraklasa');

    const firstLeague = findCompetition(
      competitions,
      (competition) => competition.season === activeSeason && Number(competition.competition_id) === 433
    ) || findCompetition(competitions, (competition) => competition.season === activeSeason && competition.name === '1. Liga');

    const secondLeague = findCompetition(
      competitions,
      (competition) => competition.season === activeSeason && Number(competition.competition_id) === 434
    ) || findCompetition(competitions, (competition) => competition.season === activeSeason && competition.name === '2. Liga');

    const europaLeague = findCompetition(
      competitions,
      (competition) => competition.season === activeSeason && Number(competition.competition_id) === 837
    );

    return [
      { title: 'Ekstraklasa', image: leagueImages.ekstraklasa, competition: ekstraklasa, fallbackPath: leaguePath(432, 'Ekstraklasa') },
      { title: '1. Liga', image: leagueImages.liga1, competition: firstLeague, fallbackPath: leaguePath(433, 'I liga') },
      { title: '2. Liga', image: leagueImages.liga2, competition: secondLeague, fallbackPath: leaguePath(434, 'II liga') },
      { title: 'Liga Europy', image: leagueImages.ekstraklasa, competition: europaLeague, fallbackPath: leaguePath(837, 'Liga Europy faza ligowa') },
    ];
  }, [competitions, activeSeason]);

  useEffect(() => {
    let active = true;

    setTeamsLoading(true);
    setTeamsError(null);

    fetchTeams({ countryId: 40 })
      .then((result) => {
        if (!active) return;
        setTeams(
          (result || [])
            .filter((team) => team.teamId && team.teamName)
            .map((team) => ({
              ...team,
              normalizedName: normalizeSearchText(team.teamName),
            }))
            .sort((a, b) => a.teamName.localeCompare(b.teamName, 'pl'))
        );
      })
      .catch((requestError) => {
        if (!active) return;
        console.error('Teams request failed:', requestError);
        setTeamsError('Nie udało się pobrać listy drużyn.');
      })
      .finally(() => {
        if (active) setTeamsLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    setArticlesLoading(true);
    setArticlesError(null);

    fetchArticles({ page: 1, pageSize: 3 })
      .then((result) => {
        if (!active) return;
        setArticles(result.articles || []);
      })
      .catch((requestError) => {
        if (!active) return;
        console.error('Strapi latest articles request failed:', requestError);
        setArticlesError('Nie udało się pobrać artykułów.');
      })
      .finally(() => {
        if (active) setArticlesLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  return (
    <Stack spacing={{ xs: 3, md: 4 }}>
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 2,
          border: '1px solid rgba(255,255,255,0.08)',
          bgcolor: '#0c1215',
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Box component="img" src={HOME_HERO_LARGE} srcSet={HOME_HERO_SRC_SET} sizes={HOME_HERO_SIZES}
          alt="" aria-hidden="true" fetchpriority="high" decoding="async"
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
        <Box aria-hidden="true" sx={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(90deg, rgba(8, 12, 15, 0.96), rgba(8, 12, 15, 0.74), rgba(8, 12, 15, 0.9))' }} />
        <Box sx={{ position: 'relative', maxWidth: 780, mx: 'auto', textAlign: 'center' }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 950,
              lineHeight: 1.05,
          fontSize: { xs: '1.8rem', sm: '2.35rem', md: '2.85rem' },
              mb: 1.5,
            }}
          >
            Symulacje polskich lig piłkarskich
          </Typography>
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '0.98rem', md: '1.05rem' },
              fontWeight: 700,
              mb: 3,
              textAlign: 'center',
            }}
          >
            Tabele | Terminarze | Predykcje | Ranking Elo | Serie | Analizy klubów
          </Typography>
          <GlobalSearch
            teams={teams}
            teamsLoading={teamsLoading}
            teamsError={teamsError}
            competitions={competitions}
            activeSeason={activeSeason}
          />
        </Box>
      </Paper>

      <Grid container spacing={2}>
        {actionTiles.map((tile) => (
          <Grid item xs={12} sm={6} xl={3} key={tile.title}>
            <QuickAction tile={tile} />
          </Grid>
        ))}
      </Grid>

      <Box id="rozgrywki">
        <SectionHeader
          eyebrow="Najkrótsza droga do danych"
          title="Polskie rozgrywki"
        />
        <Grid container spacing={2}>
          {featuredCompetitions.map((league) => (
            <Grid item xs={12} sm={6} md={3} key={league.title}>
              <LeagueCard {...league} />
            </Grid>
          ))}
        </Grid>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 2,
          border: '1px solid rgba(255,255,255,0.08)',
          bgcolor: 'background.surface',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <InsightsRoundedIcon sx={{ color: 'accent.green' }} />
              <Typography sx={{ fontWeight: 900, fontSize: '1.12rem' }}>
                Chcesz szybko znaleźć nietypowe serie i rekordy?
              </Typography>
            </Stack>
            <Typography sx={{ color: 'text.secondary' }}>
              Wybryki statystyczne zbierają rankingi formy, bramek, pass i anomalii, które trudno znaleźć w zwykłej tabeli.
            </Typography>
          </Box>
          <Button component={Link} to="/achievements-list" variant="contained" endIcon={<ArrowForwardRoundedIcon />}>
            Przejdź do wybryków
          </Button>
        </Stack>
      </Paper>

      <Box>
        <SectionHeader title="Najnowsze artykuły" action="Wszystkie artykuły" actionTo="/blog" />
        {articlesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress color="success" />
          </Box>
        ) : articlesError ? (
          <Alert severity="error">{articlesError}</Alert>
        ) : articles.length === 0 ? (
          <Alert severity="info">Brak artykułów.</Alert>
        ) : (
          <Grid container spacing={2}>
            {articles.map((article) => (
              <Grid item xs={12} md={4} key={article.id}>
                <Card
                  sx={{
                    height: '100%',
                    bgcolor: 'background.surface',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 2,
                  }}
                >
                  <CardActionArea component={Link} to={`/blog/${article.slug}`} sx={{ height: '100%' }}>
                    <CardMedia
                      component="img"
                      loading="lazy"
                      decoding="async"
                      height="164"
                      image={article.cover?.thumbnailUrl || article.cover?.url || defaultArticleImage}
                      srcSet={article.cover?.thumbnailSrcSet}
                      sizes="(min-width: 1200px) 320px, (min-width: 900px) 33vw, 100vw"
                      alt={article.cover?.alternativeText || article.title}
                      sx={{ objectFit: 'cover', bgcolor: 'background.default' }}
                    />
                    <CardContent sx={{ minHeight: 205, display: 'flex', flexDirection: 'column' }}>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                        {article.category?.name && <Chip label={article.category.name} size="small" />}
                        {article.publishedAt && (
                          <Chip label={formatArticleDate(article.publishedAt)} size="small" variant="outlined" />
                        )}
                      </Stack>
                      <Typography variant="h6" sx={{ fontWeight: 850, lineHeight: 1.18, mb: 1 }}>
                        {article.title}
                      </Typography>
                      <Typography sx={{ color: 'text.secondary', fontSize: '0.92rem', flexGrow: 1 }}>
                        {article.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Stack>
  );
};

export default HomePage;
